/**
 * ConfirmModal – reusable confirmation modal (e.g. delete file).
 * Aligns with Figma: title, body message, close icon, Cancel (text) + Confirm (primary) buttons.
 */

import React from "react";
import { Box, Typography, IconButton } from "@mui/material";
import Modal from "./Modal";
import Button from "./Button";
import { CloseIcon } from "./icons";
import { colors } from "./colors";

export interface ConfirmModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel?: () => void;
  disabled?: boolean;
  loading?: boolean;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  open,
  onClose,
  title,
  message,
  confirmLabel,
  cancelLabel = "Cancel",
  onConfirm,
  onCancel,
  disabled = false,
  loading = false,
}): JSX.Element => {
  const handleCancel = (): void => {
    (onCancel ?? onClose)();
  };

  return (
    <Modal open={open} onClose={onClose} variant="compact" keepMounted>
      <Box
        sx={{
          padding: "24px",
          display: "flex",
          flexDirection: "column",
          gap: "24px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              color: colors.neutral.white,
              fontFamily: "Outfit, sans-serif",
              fontSize: "18px",
              fontWeight: 600,
            }}
          >
            {title}
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: colors.neutral[400],
              "&:hover": { backgroundColor: colors.background[700] },
            }}
            aria-label="Close"
          >
            <CloseIcon size={20} />
          </IconButton>
        </Box>

        <Typography
          sx={{
            color: colors.neutral[400],
            fontFamily: "Outfit, sans-serif",
            fontSize: "14px",
            fontWeight: 400,
            lineHeight: 1.5,
          }}
        >
          {message}
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
          <Button
            variant="text"
            onClick={handleCancel}
            sx={{
              borderRadius: "100px",
              "&:hover": { backgroundColor: colors.background[700] },
            }}
          >
            {cancelLabel}
          </Button>
          <Button variant="primary" onClick={onConfirm} disabled={disabled} loading={loading}>
            {confirmLabel}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default ConfirmModal;
